import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, TrendingDown, Package, Sparkles, RefreshCw, ShoppingBag, Search, FileSpreadsheet, Hourglass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";
import PDFExportButton from "@/components/PDFExportButton";

/**
 * Panel Bajo Stock — vista del director con productos ordenados por
 * dias-hasta-agotar segun consumo real de los ultimos N dias.
 */
export default function PanelStockPage() {
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [filtro, setFiltro] = useState("todos");
  const [busqueda, setBusqueda] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/productos/_/panel-bajo-stock?dias=${dias}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el panel");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dias]);

  const analizar = async () => {
    try {
      const { data } = await api.post("/productos/_/analizar-bajo-stock");
      toast.success(`${data.productos_analizados} productos analizados. Notificaciones enviadas.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo ejecutar el análisis");
    }
  };

  const exportarReorden = async (soloCriticoAlerta = true) => {
    setExportando(true);
    try {
      const incluir = soloCriticoAlerta ? "critico,alerta" : "critico,alerta,watch";
      const { data: blob } = await api.get(
        `/productos/_/exportar-reorden-xlsx?dias=${dias}&incluir=${incluir}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `beem_reorden_china_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel de reorden generado. Envíalo a la fábrica.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el Excel");
    } finally { setExportando(false); }
  };

  const filtered = (data?.items || []).filter((r) => {
    if (filtro !== "todos" && r.severidad !== filtro) return false;
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      const nombre = String(r.nombre || "").toLowerCase();
      const sku = String(r.sku || "").toLowerCase();
      if (!nombre.includes(q) && !sku.includes(q)) return false;
    }
    return true;
  });

  const t = data?.totales || {};

  return (
    <div>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Inventario</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2 flex items-center gap-3">
            <TrendingDown className="w-8 h-8 text-primary" /> Panel de bajo stock
          </h1>
          <p className="text-muted-foreground mt-2">Productos ordenados por días hasta agotarse según el consumo real. Prioriza el próximo pedido a China.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground">Analizar últimos</label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="number" min="7" max="180" value={dias} onChange={(e) => setDias(Number(e.target.value) || 30)} className="h-11 w-24" data-testid="panel-stock-dias" />
              <span className="text-xs text-muted-foreground">días</span>
            </div>
          </div>
          <Button onClick={load} variant="outline" disabled={loading} className="h-11" data-testid="panel-stock-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
          <PDFExportButton
            endpoint="bajo-stock"
            filename="beem-bajo-stock"
            label="Exportar PDF"
            testid="export-bajo-stock-pdf"
            fields={[
              { key: "categoria", label: "Categoría", type: "select", optionsFrom: "categorias" },
            ]}
          />
          <Button
            onClick={() => exportarReorden(true)}
            disabled={exportando}
            variant="outline"
            className="h-11 border-emerald-500/40 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
            data-testid="export-reorden-xlsx"
            title="Descarga un Excel listo para enviar a la fábrica China con los productos que necesitan reorden (críticos + alerta)."
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> {exportando ? "Generando Excel…" : "Reorden a China"}
          </Button>
          <Button onClick={analizar} className="h-11 bg-primary hover:bg-primary/90" data-testid="panel-stock-analizar">
            <Sparkles className="w-4 h-4 mr-2" /> Analizar y notificar
          </Button>
        </div>
      </header>

      <UsageTip storageKey="panel-bajo-stock" steps={[
        "El ritmo se calcula sobre <b>pedidos confirmados</b> y <b>movimientos de inventario</b> de los últimos N días.",
        "<b>Analizar y notificar</b> dispara notificaciones inteligentes al director (para reordenar) y a vendedores (para avisar clientes).",
        "Las <b>unidades sugeridas</b> = ritmo × 60 días (2 meses de cobertura).",
      ]} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MetricCard label="Críticos" value={t.critico ?? 0} subtitle="≤ 7 días" tone="critico" onClick={() => setFiltro("critico")} active={filtro === "critico"} />
        <MetricCard label="Alerta" value={t.alerta ?? 0} subtitle="≤ 14 días" tone="alerta" onClick={() => setFiltro("alerta")} active={filtro === "alerta"} />
        <MetricCard label="En observación" value={t.watch ?? 0} subtitle="≤ 30 días" tone="watch" onClick={() => setFiltro("watch")} active={filtro === "watch"} />
        <MetricCard label="Reorden estimado" value={`$${(data?.valor_estimado_reorden_cop ?? 0).toLocaleString("es-CO")}`} subtitle="a costo" tone="reorden" onClick={() => {}} />
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FiltroChip label="Todos" v="todos" filtro={filtro} setFiltro={setFiltro} />
        <FiltroChip label="Críticos" v="critico" filtro={filtro} setFiltro={setFiltro} />
        <FiltroChip label="Alerta" v="alerta" filtro={filtro} setFiltro={setFiltro} />
        <FiltroChip label="En observación" v="watch" filtro={filtro} setFiltro={setFiltro} />
        <FiltroChip label="OK" v="ok" filtro={filtro} setFiltro={setFiltro} />
        <div className="relative ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input type="text" placeholder="Buscar por nombre o SKU..." value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="h-8 pl-8 pr-3 w-64 text-xs" data-testid="panel-stock-buscar" />
            {busqueda && (
              <button onClick={() => setBusqueda("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                data-testid="panel-stock-buscar-clear">✕</button>
            )}
          </div>
          <span className="text-xs text-muted-foreground">{filtered.length} productos</span>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="panel-stock-table">
            <thead className="bg-muted/40 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Producto</th>
                <th className="text-center px-4 py-3">Stock</th>
                <th className="text-center px-4 py-3">Mín.</th>
                <th className="text-center px-4 py-3">Consumo ({dias}d)</th>
                <th className="text-center px-4 py-3">Ritmo/día</th>
                <th className="text-center px-4 py-3">Días restantes</th>
                <th className="text-right px-4 py-3" title="Unidades sugeridas = ritmo/día × 60 días de cobertura. Debajo: cuánto dura esa cantidad y cuánto durarías en total (stock + sugerido)">Reorden sug. · cuánto dura</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-muted-foreground">
                    {loading ? "Analizando consumo…" : "No hay productos en este filtro"}
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-accent/30 transition-colors" data-testid={`row-stock-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <SeverityDot sev={r.severidad} />
                      <div>
                        <div className="font-medium text-foreground">{r.nombre}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{r.codigo || r.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-center px-4 py-3 tabular-nums">
                    <span className={r.stock_actual <= 0 ? "text-rose-600 font-semibold" : ""}>{r.stock_actual.toLocaleString("es-CO")}</span>
                  </td>
                  <td className="text-center px-4 py-3 text-muted-foreground tabular-nums">{r.stock_minimo.toLocaleString("es-CO")}</td>
                  <td className="text-center px-4 py-3 tabular-nums">{r.consumo_periodo.toLocaleString("es-CO")}</td>
                  <td className="text-center px-4 py-3 tabular-nums text-muted-foreground">{r.ritmo_por_dia > 0 ? r.ritmo_por_dia.toFixed(1) : "—"}</td>
                  <td className="text-center px-4 py-3">
                    <DiasRestantes dias={r.dias_restantes} />
                  </td>
                  <td className="text-right px-4 py-3">
                    {r.unidades_sugeridas_reorden > 0 ? (
                      <div>
                        <div className="font-semibold tabular-nums flex items-center justify-end gap-1">
                          <ShoppingBag className="w-3.5 h-3.5 text-primary" />
                          {r.unidades_sugeridas_reorden.toLocaleString("es-CO")}
                        </div>
                        <div className="text-[10px] text-muted-foreground tabular-nums">${r.valor_reorden_cop.toLocaleString("es-CO")}</div>
                        {r.dias_dura_sugerido != null ? (
                          <div className="text-[10px] text-primary tabular-nums inline-flex items-center gap-1" title={`Al ritmo actual (${r.ritmo_por_dia.toFixed(1)} und/día). Con lo que hay en bodega + lo sugerido te alcanzaría ≈ ${duracion(r.dias_dura_total)} en total.`} data-testid={`dura-sugerido-${r.id}`}>
                            <Hourglass className="w-3 h-3" /> dura {duracion(r.dias_dura_sugerido)} · total {duracion(r.dias_dura_total)}
                          </div>
                        ) : (
                          <div className="text-[10px] text-muted-foreground" title="Sin ventas en el período: la cantidad es para volver al mínimo">para llegar al mínimo</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, subtitle, tone, onClick, active }) {
  const tones = {
    critico: "bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400",
    alerta: "bg-amber-500/10 border-amber-500/30 text-amber-800 dark:text-amber-400",
    watch: "bg-blue-500/10 border-blue-500/30 text-blue-800 dark:text-blue-400",
    reorden: "bg-primary/10 border-primary/30 text-primary",
  };
  return (
    <button onClick={onClick} className={`text-left rounded-xl border-2 p-4 transition-all ${tones[tone]} ${active ? "ring-2 ring-offset-2 ring-primary/60" : "hover:brightness-105"}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-80">{label}</div>
      <div className="text-3xl font-semibold tabular-nums mt-2">{value}</div>
      <div className="text-xs opacity-70 mt-1">{subtitle}</div>
    </button>
  );
}

function FiltroChip({ label, v, filtro, setFiltro }) {
  const active = filtro === v;
  return (
    <button onClick={() => setFiltro(v)} data-testid={`chip-${v}`}
      className={`h-8 px-3 rounded-full text-xs font-medium border transition-all ${active ? "bg-primary text-primary-foreground border-primary" : "bg-transparent border-border hover:bg-accent"}`}>
      {label}
    </button>
  );
}

function SeverityDot({ sev }) {
  const colors = {
    critico: "bg-rose-500",
    alerta: "bg-amber-500",
    watch: "bg-blue-500",
    ok: "bg-emerald-500",
  };
  return <span className={`w-2 h-2 rounded-full inline-block ${colors[sev] || "bg-muted"}`} />;
}

const duracion = (d) => {
  if (d == null) return "—";
  if (d < 14) return `${d} ${d === 1 ? "día" : "días"}`;
  if (d < 60) return `${Math.round(d / 7)} sem`;
  return `${(d / 30).toFixed(d < 90 ? 1 : 0)} meses`;
};

function DiasRestantes({ dias }) {
  if (dias >= 999) return <span className="text-xs text-muted-foreground">Sin consumo</span>;
  let cls = "text-emerald-700";
  let icon = null;
  if (dias <= 7) { cls = "text-rose-600 font-semibold"; icon = <AlertTriangle className="w-3.5 h-3.5" />; }
  else if (dias <= 14) cls = "text-amber-700 font-semibold";
  else if (dias <= 30) cls = "text-blue-700";
  return (
    <span className={`inline-flex items-center gap-1 tabular-nums ${cls}`}>
      {icon}{dias} {dias === 1 ? "día" : "días"}
    </span>
  );
}
